import Roles from "../../../shared/models/roles";
import {
    URL_ACCOUNT,
    URL_AREA_MANAGER,
    URL_BADGES,
    URL_BOOKINGS,
    URL_CERTIFICATES,
    URL_CHANGELOG,
    URL_COURSES,
    URL_COURSES_BULK_SEND,
    URL_COURSES_CATEGORIES,
    URL_COURSES_PREREQUISITE,
    URL_COURSES_TYPES,
    URL_COURSES_DISCOUNT_CODES,
    URL_CUSTOMERS,
    URL_CUSTOMERS_DUPLICATES,
    URL_DASHBOARD,
    URL_HOME,
    URL_INSTRUCTOR,
    URL_INVOICE,
    URL_LESSONS_TODAY,
    URL_LOCATIONS,
    URL_MY_BOOKINGS,
    URL_NOTIFICATIONS,
    URL_NOTIFICATIONS_FIXER,
    URL_PAYMENTS,
    URL_MOLLIE_RETRY_QUEUE,
    URL_SETTINGS,
    URL_HELP,
    URL_SETTINGS_ADMIN,
    URL_SETTINGS_GENERAL,
    URL_SETTINGS_ENDUSERAPP,
    URL_SETTINGS_IMPORT_BOOKERS,
    URL_SETTINGS_DATA_EXPORT,
    URL_SETTINGS_INVOICE_EXPORT,
    URL_SETTINGS_MAINTENANCE,
    URL_SETTINGS_MONITORING,
    URL_SETTINGS_MCP,
    URL_SETTINGS_MOLLIE_DEV_DOCS,
    URL_SETUP,
    URL_STAFF,
    URL_STAFF_ADMINS,
    URL_STAFF_BILLING_STATUS,
    URL_STAFF_LOGIN,
    URL_TEMPLATES,
    URL_WELCOME_PAGE,
    URL_WITHDRAWALS,
    URL_PAYMENTS_BANK_IMPORT,
    URL_PAYMENTS_TARIF595,
    URL_INTEREST_LISTS,
    URL_NEWSLETTER,
} from "../../models/url";

const allWithoutEndUser: Array<Roles> = Object.values(Roles).filter((role) => role !== Roles.EndUser);

// Kopie von shared/models/roles.ts (Server ist die Quelle). AreaManager steht UNTER Admin (#706).
const roleHierarchy: Array<Roles> = [
    Roles.Superuser,
    Roles.SuperAdmin,
    Roles.Admin,
    Roles.AreaManager,
    Roles.Instructor,
    Roles.Staff,
    Roles.EndUser,
];

export const atLeast = (minRole: Roles): Roles[] => {
    const index = roleHierarchy.indexOf(minRole);
    if (index === -1) return [minRole];
    return roleHierarchy.slice(0, index + 1);
};

export const routePermissions: {[key: string]: Array<Roles>} = {
    [URL_HOME]: allWithoutEndUser,
    // [URL_WELCOME_PAGE]: allWithoutEndUser,
    [URL_WELCOME_PAGE]: [],
    [URL_SETUP]: allWithoutEndUser,
    [URL_COURSES]: allWithoutEndUser,
    [URL_COURSES_PREREQUISITE]: [...atLeast(Roles.Admin), Roles.BetaTester],
    [URL_COURSES_TYPES]: [...atLeast(Roles.Staff), Roles.BetaTester],
    [URL_COURSES_CATEGORIES]: [...atLeast(Roles.Staff), Roles.BetaTester],
    [URL_COURSES_BULK_SEND]: [...atLeast(Roles.Staff), Roles.BetaTester],
    // Rabattcodes (Beta): Verwaltung ab Admin, wie der Server-Resolver; zusätzlich Tenant-Flag in MainMenuRoutes.
    [URL_COURSES_DISCOUNT_CODES]: atLeast(Roles.Admin),
    [URL_CUSTOMERS]: allWithoutEndUser,
    [URL_CUSTOMERS_DUPLICATES]: [Roles.Superuser],
    [URL_LOCATIONS]: [...atLeast(Roles.Staff), Roles.BetaTester],
    [URL_BOOKINGS]: [...atLeast(Roles.Admin), Roles.BetaTester],
    [URL_MY_BOOKINGS]: allWithoutEndUser,
    [URL_INVOICE]: [...atLeast(Roles.Staff), Roles.BetaTester],
    [URL_TEMPLATES]: [...atLeast(Roles.Admin), Roles.BetaTester],
    [URL_PAYMENTS]: [...atLeast(Roles.Admin), Roles.BetaTester],
    // Bankabgleich (Beta): bewusst nur SuperAdmin+Superuser (Entscheidung 07.10.2026)
    [URL_PAYMENTS_BANK_IMPORT]: atLeast(Roles.SuperAdmin),
    [URL_PAYMENTS_TARIF595]: atLeast(Roles.Admin),
    // Widerrufe nur für Admin/Superuser (atLeast(Admin) enthält Superuser).
    [URL_WITHDRAWALS]: [...atLeast(Roles.Admin), Roles.BetaTester],
    // Interessenten-Listen: wie Buchungen (Admin/Staff). Menü zusätzlich
    // Feature-Flag-gated (featureInterestList) in MainMenuRoutes.
    [URL_INTEREST_LISTS]: [...atLeast(Roles.Staff), Roles.BetaTester],
    // Newsletter (Beta): ab Admin, Menü zusätzlich Feature-Flag-gated (featureNewsletter) in MainMenuRoutes.
    [URL_NEWSLETTER]: atLeast(Roles.Admin),
    // Alte URL, leitet nur auf die Chargeback-Inbox unter URL_PAYMENTS (Admin-Gruppe) weiter (#709).
    [URL_MOLLIE_RETRY_QUEUE]: [...atLeast(Roles.Admin), Roles.BetaTester],
    [URL_STAFF]: allWithoutEndUser,
    [URL_STAFF_LOGIN]: allWithoutEndUser,
    [URL_STAFF_BILLING_STATUS]: [...atLeast(Roles.Admin), Roles.BetaTester],
    // Self-Service-Admin-Verwaltung: bewusst NUR SuperAdmin+Superuser (Design 31.08.2026).
    [URL_STAFF_ADMINS]: atLeast(Roles.SuperAdmin),
    [URL_SETTINGS]: [...atLeast(Roles.Admin), Roles.BetaTester],
    [URL_SETTINGS_ENDUSERAPP]: [...atLeast(Roles.Admin), Roles.BetaTester],
    [URL_SETTINGS_ADMIN]: [Roles.Superuser],
    [URL_SETTINGS_GENERAL]: [...atLeast(Roles.Admin), Roles.BetaTester],
    [URL_SETTINGS_IMPORT_BOOKERS]: [Roles.Superuser],
    [URL_SETTINGS_DATA_EXPORT]: atLeast(Roles.SuperAdmin),
    [URL_SETTINGS_INVOICE_EXPORT]: atLeast(Roles.SuperAdmin),
    [URL_SETTINGS_MAINTENANCE]: [Roles.Superuser],
    [URL_SETTINGS_MONITORING]: [Roles.Superuser],
    // KI-Assistent (Claude-Connector): Verbindungsdaten für Admins; Menü zusätzlich Feature-Flag-gated (featureMcp).
    [URL_SETTINGS_MCP]: atLeast(Roles.Admin),
    [URL_SETTINGS_MOLLIE_DEV_DOCS]: [Roles.Superuser],
    [URL_NOTIFICATIONS]: [...atLeast(Roles.Admin), Roles.BetaTester],
    [URL_NOTIFICATIONS_FIXER]: [Roles.Superuser],
    [URL_CHANGELOG]: allWithoutEndUser,
    [URL_HELP]: allWithoutEndUser,
    [URL_BADGES]: [...atLeast(Roles.Admin), Roles.BetaTester],
    [URL_CERTIFICATES]: [...atLeast(Roles.Admin), Roles.BetaTester],
    [URL_ACCOUNT]: allWithoutEndUser,
    [URL_MY_BOOKINGS]: [...atLeast(Roles.Instructor), Roles.BetaTester],
    [URL_INSTRUCTOR]: [...atLeast(Roles.Instructor), Roles.BetaTester],
    // [URL_LESSONS_TODAY]: [Roles.Superuser, Roles.BetaTester],
    [URL_LESSONS_TODAY]: allWithoutEndUser,
    [URL_AREA_MANAGER]: [...atLeast(Roles.AreaManager), Roles.BetaTester],
    [URL_DASHBOARD]: [...atLeast(Roles.Admin), Roles.BetaTester],
};
